import type { ReactNode } from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@/lib/utils";

export interface TabItem {
  value: string;
  label: string;
  /** Optional mono count shown after the label. */
  count?: number;
}

interface TabsProps {
  items: TabItem[];
  /** Accessible name of the tab list. */
  label: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** `TabsPanel`s, one per tab value. */
  children?: ReactNode;
  className?: string;
}

/** Underline tabs (Radix) with an optional count per tab. */
export function Tabs({ items, label, value, defaultValue, onValueChange, children, className }: TabsProps) {
  return (
    <TabsPrimitive.Root
      value={value}
      defaultValue={defaultValue ?? items[0]?.value}
      onValueChange={onValueChange}
      className={cn("font-ds", className)}
    >
      <TabsPrimitive.List aria-label={label} className="mb-4 flex gap-0.5 overflow-x-auto border-b border-ds-line">
        {items.map((item) => (
          <TabsPrimitive.Trigger
            key={item.value}
            value={item.value}
            className={cn(
              "relative h-9 whitespace-nowrap px-3 text-[13.5px] font-semibold text-ds-ink3 transition-colors hover:text-ds-ink",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-accent",
              "data-[state=active]:text-ds-ink data-[state=active]:after:absolute data-[state=active]:after:inset-x-2 data-[state=active]:after:-bottom-px data-[state=active]:after:h-0.5 data-[state=active]:after:rounded-sm data-[state=active]:after:bg-ds-accent"
            )}
          >
            {item.label}
            {item.count !== undefined && (
              <span className="ml-1 font-ds-mono text-[11px] font-medium text-ds-ink3"> {item.count}</span>
            )}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
      {children}
    </TabsPrimitive.Root>
  );
}

interface TabsPanelProps {
  value: string;
  children: ReactNode;
  className?: string;
}

/** The content of one tab. */
export function TabsPanel({ value, children, className }: TabsPanelProps) {
  return (
    <TabsPrimitive.Content value={value} className={cn("focus-visible:outline-none", className)}>
      {children}
    </TabsPrimitive.Content>
  );
}
